'use client';

import { useEffect, useRef } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';

const SWITCH_STATE = {
  FIRST_LOAD: 'first_load',
  BUY: 'buy',
  SELL: 'sell',
} as const;

type SwitchState = (typeof SWITCH_STATE)[keyof typeof SWITCH_STATE];

export function HomepageSwitch({
  checked,
  moduleName = 'homepage_hero_switch',
  buyHref = '/',
  sellHref = '/start-to-sell',
  variant,
}: {
  checked: boolean;
  moduleName?: string;
  buyHref?: string;
  sellHref?: string;
  variant?: string | null;
}) {
  const buyRef = useRef<HTMLAnchorElement>(null);
  const sellRef = useRef<HTMLAnchorElement>(null);
  const previousState = useRef<SwitchState>(SWITCH_STATE.FIRST_LOAD);
  const { sendActivityTrackerEvent } = useActivityTracker();
  const t = useTranslations('home');

  useEffect(() => {
    if (previousState.current === SWITCH_STATE.FIRST_LOAD) {
      previousState.current = checked ? SWITCH_STATE.SELL : SWITCH_STATE.BUY;
    } else if (previousState.current === SWITCH_STATE.SELL) {
      requestAnimationFrame(() => {
        buyRef.current?.focus({ preventScroll: true });
      });
      previousState.current = SWITCH_STATE.BUY;
    } else {
      requestAnimationFrame(() => {
        sellRef.current?.focus({ preventScroll: true });
      });
      previousState.current = SWITCH_STATE.SELL;
    }
  }, [checked]);

  function handleSwitch(type: SwitchState) {
    window.scrollTo(0, 0);
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      { interactionType: type, moduleName }
    );
  }

  return (
    <nav className={styles.container}>
      <span
        className={styles.thumb}
        data-checked={checked}
        data-variant={variant}
      />
      <ExtendedLink
        ref={buyRef}
        prefetch
        href={buyHref}
        scroll={false}
        aria-current={!checked ? 'page' : undefined}
        onClick={() => handleSwitch(SWITCH_STATE.BUY)}
      >
        <Body variant={!checked ? 'sm-bold' : 'sm-regular'}>
          {t('DualProposition.Buy.Switch')}
        </Body>
      </ExtendedLink>
      <ExtendedLink
        ref={sellRef}
        prefetch
        href={sellHref}
        scroll={false}
        aria-current={checked ? 'page' : undefined}
        onClick={() => handleSwitch(SWITCH_STATE.SELL)}
      >
        <Body variant={checked ? 'sm-bold' : 'sm-regular'}>
          {t('DualProposition.Sell.Switch')}
        </Body>
      </ExtendedLink>
    </nav>
  );
}
